import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const BarMenu = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    category_id: '',
    item_name: '',
    description: '',
    price: '',
    cost_price: '',
    alcohol_content: '',
    volume_ml: '',
    preparation_time: 5
  });
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedCategory]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchCategories(), fetchMenuItems()]);
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const data = await apiGet('/admin/bar/menu/categories');
      if (data.success) setCategories(data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const url = selectedCategory === 'all'
        ? '/admin/bar/menu/items'
        : `/admin/bar/menu/items?category_id=${selectedCategory}`;

      const data = await apiGet(url);
      if (data.success) setMenuItems(data.data);
    } catch (error) {
      console.error('Error fetching menu items:', error);
    }
  };

  const handleSubmitItem = async (e) => {
    e.preventDefault();
    try {
      const url = editingItem
        ? `/admin/bar/menu/items/${editingItem.id}`
        : '/admin/bar/menu/items';

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Item ${editingItem ? 'updated' : 'created'} successfully!`);
        setShowItemModal(false);
        setEditingItem(null);
        resetForm();
        fetchData();
      }
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      category_id: item.category_id,
      item_name: item.item_name,
      description: item.description || '',
      price: item.price,
      cost_price: item.cost_price,
      alcohol_content: item.alcohol_content || '',
      volume_ml: item.volume_ml || '',
      preparation_time: item.preparation_time
    });
    setShowItemModal(true);
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const data = await apiDelete(`/admin/bar/menu/items/${itemId}`);
      if (data.success) {
        setSuccessMessage('✅ Item deleted successfully!');
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const toggleAvailability = async (itemId, currentStatus) => {
    try {
      const response = await fetch(`/admin/bar/menu/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_available: !currentStatus })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Item ${!currentStatus ? 'enabled' : 'disabled'} successfully!`);
        fetchData();
      }
    } catch (error) {
      console.error('Error toggling availability:', error);
    }
  };

  const resetForm = () => {
    setItemForm({
      category_id: '',
      item_name: '',
      description: '',
      price: '',
      cost_price: '',
      alcohol_content: '',
      volume_ml: '',
      preparation_time: 5
    });
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    filterContainer: {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
      overflowX: 'auto',
      paddingBottom: '4px'
    },
    filterButton: {
      padding: isMobile ? '8px 12px' : '10px 16px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      background: colors.white,
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap'
    },
    activeFilter: {
      background: colors.primary,
      color: colors.white,
      borderColor: colors.primary
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '12px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    tableCell: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '12px',
      fontWeight: '600',
      color: colors.textDark
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit'
    },
    button: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '6px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading menu...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {successMessage && (
        <div style={{
          padding: isMobile ? '12px 16px' : '14px 20px',
          borderRadius: '10px',
          marginBottom: '16px',
          fontSize: isMobile ? '13px' : '14px',
          fontWeight: '600',
          background: 'rgba(74, 124, 42, 0.15)',
          color: colors.success,
          border: `2px solid ${colors.success}`,
          margin: isMobile ? '0 4px 12px 4px' : '0 0 16px 0'
        }}>
          {successMessage}
        </div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Bar Menu</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Drinks & cocktails' : 'Manage drinks, cocktails, and beverage menu items'}
        </p>
      </div>

      {/* Categories Summary */}
      <div style={styles.contentCard}>
        <h2 style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>📂</span> Categories
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '12px' }}>
          {categories.map(cat => (
            <div key={cat.id} style={{
              padding: isMobile ? '14px' : '16px',
              background: colors.lightGray,
              borderRadius: '10px',
              border: `1px solid ${colors.mediumGray}`
            }}>
              <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', marginBottom: '6px', color: colors.primary }}>
                {cat.category_name}
              </div>
              <div style={{ fontSize: '12px', color: colors.textLight }}>
                {cat.items_count} item{cat.items_count !== 1 ? 's' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Menu Items */}
      <div style={styles.contentCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
            <span>🍹</span> Menu Items
          </h2>
          <button
            onClick={() => {
              setEditingItem(null);
              resetForm();
              setShowItemModal(true);
            }}
            style={{
              ...styles.button,
              background: colors.primary,
              color: colors.white
            }}
          >
            + Add Item
          </button>
        </div>

        {/* Category Filter */}
        <div style={styles.filterContainer}>
          <button
            onClick={() => setSelectedCategory('all')}
            style={{
              ...styles.filterButton,
              ...(selectedCategory === 'all' ? styles.activeFilter : {})
            }}
          >
            All Items
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              style={{
                ...styles.filterButton,
                ...(selectedCategory === cat.id ? styles.activeFilter : {})
              }}
            >
              {cat.category_name}
            </button>
          ))}
        </div>

        {menuItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No menu items found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Name</th>
                  {!isMobile && <th style={styles.tableHeader}>Category</th>}
                  <th style={styles.tableHeader}>Price</th>
                  {!isMobile && <th style={styles.tableHeader}>ABV</th>}
                  <th style={styles.tableHeader}>Status</th>
                  <th style={styles.tableHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {menuItems.map(item => (
                  <tr key={item.id} style={{ opacity: item.is_available ? 1 : 0.6 }}>
                    <td style={styles.tableCell}>
                      <strong>{item.item_name}</strong>
                      {isMobile && (
                        <div style={{ fontSize: '11px', color: colors.textLight }}>
                          {item.category_name}
                        </div>
                      )}
                    </td>
                    {!isMobile && (
                      <td style={styles.tableCell}>{item.category_name}</td>
                    )}
                    <td style={styles.tableCell}>
                      <strong>₹{parseFloat(item.price).toFixed(2)}</strong>
                    </td>
                    {!isMobile && (
                      <td style={styles.tableCell}>
                        {item.alcohol_content ? `${item.alcohol_content}%` : 'N/A'}
                      </td>
                    )}
                    <td style={styles.tableCell}>
                      <button
                        onClick={() => toggleAvailability(item.id, item.is_available)}
                        style={{
                          ...styles.button,
                          background: item.is_available ? colors.success : colors.danger,
                          color: colors.white,
                          fontSize: '11px',
                          padding: '6px 12px'
                        }}
                      >
                        {item.is_available ? '✓ Available' : '✕ Unavailable'}
                      </button>
                    </td>
                    <td style={styles.tableCell}>
                      <button
                        onClick={() => handleEditItem(item)}
                        style={{
                          ...styles.button,
                          background: colors.accent,
                          color: colors.white,
                          fontSize: '11px',
                          padding: '6px 12px',
                          marginRight: '6px'
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        style={{
                          ...styles.button,
                          background: colors.danger,
                          color: colors.white,
                          fontSize: '11px',
                          padding: '6px 12px'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Item Modal */}
      {showItemModal && (
        <div style={styles.modal} onClick={() => setShowItemModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              {editingItem ? 'Edit Item' : 'Add New Item'}
            </h3>

            <form onSubmit={handleSubmitItem}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Category *</label>
                <select
                  value={itemForm.category_id}
                  onChange={(e) => setItemForm({ ...itemForm, category_id: e.target.value })}
                  style={{ ...styles.input, background: colors.white }}
                  required
                >
                  <option value="">Select category...</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Item Name *</label>
                <input
                  type="text"
                  value={itemForm.item_name}
                  onChange={(e) => setItemForm({ ...itemForm, item_name: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  value={itemForm.description}
                  onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                  style={{ ...styles.input, minHeight: '80px' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Price (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={itemForm.price}
                    onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Cost Price (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={itemForm.cost_price}
                    onChange={(e) => setItemForm({ ...itemForm, cost_price: e.target.value })}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Alcohol % (ABV)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={itemForm.alcohol_content}
                    onChange={(e) => setItemForm({ ...itemForm, alcohol_content: e.target.value })}
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Volume (ml)</label>
                  <input
                    type="number"
                    value={itemForm.volume_ml}
                    onChange={(e) => setItemForm({ ...itemForm, volume_ml: e.target.value })}
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Prep Time (min)</label>
                  <input
                    type="number"
                    value={itemForm.preparation_time}
                    onChange={(e) => setItemForm({ ...itemForm, preparation_time: e.target.value })}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button
                  type="submit"
                  style={{
                    ...styles.button,
                    background: colors.primary,
                    color: colors.white,
                    flex: 1
                  }}
                >
                  {editingItem ? 'Update Item' : 'Add Item'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowItemModal(false)}
                  style={{
                    ...styles.button,
                    background: colors.mediumGray,
                    color: colors.textDark,
                    flex: 1
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default BarMenu;
